// EmailTemplateSelection.jsx — original card/grid UI with single *global* search bar
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Drawer,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import RefreshIcon from "../../../styles/icons/RefreshIcon";

export default function EmailTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
  isDrawer = false,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.text.primary;

  /** ------------------------------------------------------------------
   * Global search state & logic
   * ------------------------------------------------------------------ */
  const [query, setQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [temp, setTemp] = useState(null);
  const [selectedTpl, setSelectedTpl] = useState(null);

  const fallbackImageSrc = "/path/to/fallback.png";

  // Reset selection states when modal closes
  useEffect(() => {
    if (!open) {
      setTemp(null);
      setSelectedTpl(null);
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setFilteredItems(templates);
      return;
    }

    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

    const matchesAllTokens = (item) => {
      const haystacks = [
        item.Id?.toString().toLowerCase() ?? "",
        item.Name?.toLowerCase() ?? "",
        (item.Content ?? item.HtmlBody ?? "").toLowerCase(),
        (item.Description ?? "").toLowerCase(),
      ];
      return tokens.every((token) => haystacks.some((h) => h.includes(token)));
    };

    setFilteredItems(templates.filter(matchesAllTokens));
  }, [query, templates]);

  const handleSubmit = () => {
    if (selectedTpl) {
      setSelected(selectedTpl);
      setClosed();
    }
  };

  const content = (
    <>
      {/* Sticky Search Bar */}
      <Box
        sx={{
          backgroundColor: "background.default",
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid #ccc",
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TextField
          label="Search"
          placeholder="Id, name, content …"
          size="small"
          variant="outlined"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <IconButton aria-label="Refresh" onClick={refreshTemplates}>
          <RefreshIcon sx={{ color: "text.primary" }} />
        </IconButton>
      </Box>

      {/* Scrollable Card Grid */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: "background.paper",
          overflowY: "auto",
          p: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gridAutoRows: "1fr", // ← ADDED THIS
          gap: 3,
        }}
      >
        {filteredItems.length ? (
          filteredItems.map((item) => {
            const isSelected = selectedTpl?.Id === item.Id;

            return (
              <Box
                key={item.Id}
                onClick={() => setSelectedTpl(item)}
                sx={{
                  border: isSelected
                    ? "2px solid #0da2e1"
                    : `1px solid ${theme.palette.text.primary}`,
                  borderRadius: 2,
                  bgcolor: "background.default",
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                  minHeight: 200,
                  height: "50%",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.2s",
                  overflow: "hidden",
                  opacity: 1,
                  filter: "none",
                  position: "relative",
                  "&:hover": {
                    boxShadow: "0px 6px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {/* Title - Fixed height for all titles */}
                <Box
                  sx={{
                    minHeight: 48,
                    maxHeight: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    borderBottom: 1,
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title={item.Name} placement="top">
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textAlign: "center",
                        color: primaryColor,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item.Name}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Image Preview - Scrollable */}
                <Box
                  sx={{
                    flexGrow: 1,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    component="img"
                    src={item.PreviewUrl}
                    alt={item.Name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = fallbackImageSrc;
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>

                {/* Status Bar - Full Width */}
                <Box
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 0.1,
                    backgroundColor: "#0da2e1",
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    borderBottomLeftRadius: 6,
                    borderBottomRightRadius: 6,
                    flexShrink: 0,
                  }}
                >
                  Available
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography>No Templates Found</Typography>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "row-reverse", gap: 1 }}>
        <Button
          variant="contained"
          disabled={!selectedTpl}
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            backgroundColor: theme.palette.companyBlue?.main || "#0da2e1",
            color: "text.primary",
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            color: "text.primary",
          }}
          onClick={setClosed}
        >
          Cancel
        </Button>
      </Box>
    </>
  );

  if (isDrawer) {
    return (
      <Drawer
        open={open}
        onClose={setClosed}
        anchor="left"
        PaperProps={{
          sx: {
            width: "800px",
            maxWidth: "90vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          width: 800,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {content}
    </Dialog>
  );
}
